import { Check, Copy } from 'lucide-react'
import * as React from 'react'
import { Button } from './button'

export type ResultCell = string | number | boolean | null

export interface ResultCaseLink {
  case_id: string
  case_number: string
  href: string
  label: string
}

export interface ResultColumn {
  key: string
  label: string
  align?: 'start' | 'end'
}

export interface ResultRow {
  id: string
  case: ResultCaseLink | null
  cells: Record<string, ResultCell>
}

export interface ResultsTableData {
  kind: 'results_table'
  title: string
  sensitivity: 'staff' | 'money'
  columns: ResultColumn[]
  rows: ResultRow[]
  csv_filename: string
  next_cursor?: string | null
}

const display = (value: ResultCell | undefined): string => (value == null ? '' : String(value))

/** Prevent a copied value from becoming a formula when the CSV is opened in a spreadsheet. */
export function safeCsvCell(value: ResultCell | undefined): string {
  let text = display(value)
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`
  return `"${text.replace(/"/g, '""')}"`
}

export function resultsTableCsv(table: ResultsTableData): string {
  const hasCases = table.rows.some((row) => row.case)
  const header = [...(hasCases ? ['Case'] : []), ...table.columns.map((column) => column.label)]
  const rows = table.rows.map((row) => [
    ...(hasCases ? [row.case?.case_number ?? ''] : []),
    ...table.columns.map((column) => row.cells[column.key]),
  ])
  return [header, ...rows].map((row) => row.map(safeCsvCell).join(',')).join('\r\n')
}

export function ResultsTable({ table }: { table: ResultsTableData }) {
  const [copied, setCopied] = React.useState(false)
  const hasCases = table.rows.some((row) => row.case)
  const copy = async () => {
    await navigator.clipboard.writeText(resultsTableCsv(table))
    setCopied(true)
    globalThis.setTimeout(() => setCopied(false), 1500)
  }
  return (
    <section className="overflow-hidden rounded-md border border-border bg-card" aria-label={table.title}>
      <div className="flex items-center justify-between gap-3 border-b border-border bg-muted/50 px-3 py-2">
        <h3 className="min-w-0 truncate text-sm font-semibold">{table.title}</h3>
        <Button type="button" variant="ghost" size="sm" onClick={() => void copy()} aria-label={`Copy ${table.title} as CSV`}>
          {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
          {copied ? 'Copied' : 'Copy CSV'}
        </Button>
      </div>
      <div className="max-w-full min-w-0 overflow-x-auto overscroll-x-contain">
        <table className="w-max min-w-full text-sm">
          <thead className="bg-muted text-left">
            <tr>
              {hasCases ? <th className="px-3 py-1.5 font-semibold">Case</th> : null}
              {table.columns.map((column) => (
                <th key={column.key} className={`px-3 py-1.5 font-semibold ${column.align === 'end' ? 'text-right' : ''}`}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row) => (
              <tr key={row.id} className="border-t border-border">
                {hasCases ? (
                  <td className="whitespace-nowrap px-3 py-1.5">
                    {row.case ? (
                      <a className="font-medium text-primary hover:underline" href={row.case.href} title={row.case.label}>
                        {row.case.case_number}
                      </a>
                    ) : null}
                  </td>
                ) : null}
                {table.columns.map((column) => (
                  <td
                    key={column.key}
                    className={`max-w-80 px-3 py-1.5 [overflow-wrap:anywhere] ${column.align === 'end' ? 'text-right tabular-nums' : ''}`}
                  >
                    {display(row.cells[column.key])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {table.next_cursor ? <p className="border-t border-border px-3 py-1.5 text-xs text-subtle">More results are available.</p> : null}
    </section>
  )
}

export function ResultsTables({ tables }: { tables?: ResultsTableData[] }) {
  if (!tables?.length) return null
  return (
    <div className="space-y-2">
      {tables.map((table) => (
        <ResultsTable key={`${table.title}:${table.csv_filename}`} table={table} />
      ))}
    </div>
  )
}
